import { useState } from 'react';
import { Alert, Button, Card, Col, Container, Row } from 'react-bootstrap';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { BadDogAuthHandoffFailure, handoffBadDogAuthToken } from '../../services/badDogAuthHandoff';
import {
    clearCachedAccessTokenProps,
    setAccessToken,
    setAvailableTenants,
    setRefreshToken,
    setTenantSelectionPending,
} from '../../utils/tokenUtils';
import { fetchAvailableTenants } from '../../utils/tenantSwitchUtils';

const AuthorizationServiceUnavailable = () => {
    const { instance } = useMsal();
    const navigate = useNavigate();
    const [isRetrying, setIsRetrying] = useState(false);
    const [retryMessage, setRetryMessage] = useState('');

    const handleRetry = async () => {
        const account = instance.getActiveAccount();
        if (!account) {
            setRetryMessage('Your sign-in session is no longer available. Please sign in again.');
            return;
        }

        setIsRetrying(true);
        setRetryMessage('');

        try {
            const tokenResponse = await instance.acquireTokenSilent({
                account,
                scopes: ['openid', 'profile', 'email'],
            });
            const badDogTokens = await handoffBadDogAuthToken(tokenResponse.idToken);

            setAccessToken(badDogTokens.access_token);
            setRefreshToken(badDogTokens.refresh_token);

            let requiresTenantSelection = false;
            try {
                const tenants = await fetchAvailableTenants();
                setAvailableTenants(tenants);
                requiresTenantSelection = Array.isArray(tenants) && tenants.length > 1;
                setTenantSelectionPending(requiresTenantSelection);
            } catch (tenantFetchError) {
                console.warn('Could not fetch available tenants after retry; proceeding as single-tenant:', tenantFetchError);
                setTenantSelectionPending(false);
            }

            navigate(requiresTenantSelection ? '/select-tenant' : '/landing', { replace: true });
        } catch (error) {
            console.error('BADDOG handoff retry failed:', error);
            clearCachedAccessTokenProps();

            if (error.kind === BadDogAuthHandoffFailure.ACCOUNT_NOT_CONFIGURED) {
                navigate('/ErrorAccountNotConfigured', { replace: true });
                return;
            }

            setRetryMessage('Telescope is still unavailable. Please try again in a moment.');
        } finally {
            setIsRetrying(false);
        }
    };

    const handleSignOut = () => {
        clearCachedAccessTokenProps();
        instance.logoutRedirect({ postLogoutRedirectUri: '/' });
    };

    return (
        <Container fluid className="py-5">
            <Row className="justify-content-center">
                <Col md={9} lg={7} xl={6}>
                    <Card className="border-0 shadow-sm">
                        <Card.Body className="p-4 p-md-5 text-center">
                            <div className="mb-3 text-warning" aria-hidden="true">
                                <i className="bi bi-cloud-slash-fill" style={{ fontSize: '3rem' }}></i>
                            </div>
                            <h1 className="h3 mb-3">Telescope is temporarily unavailable</h1>
                            <p className="lead mb-4">
                                You signed in successfully, but we cannot finish setting up your Telescope session right now.
                            </p>
                            <p className="text-muted mb-4">
                                Please try again in a moment. If the problem continues, contact your Telescope administrator or support.
                            </p>

                            {retryMessage && (
                                <Alert variant="warning" role="status" className="text-start">
                                    {retryMessage}
                                </Alert>
                            )}

                            <div className="d-flex justify-content-center gap-3 flex-wrap">
                                <Button variant="primary" onClick={handleRetry} disabled={isRetrying}>
                                    {isRetrying ? 'Trying again...' : 'Try again'}
                                </Button>
                                <Button variant="outline-secondary" onClick={handleSignOut} disabled={isRetrying}>
                                    Sign out
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default AuthorizationServiceUnavailable;